/**
 * Everything a native shell has to do that a browser does for free.
 *
 * Four concerns, all of them things a user notices immediately when they are missing: the back
 * gesture, the status bar, the keyboard, and the splash screen. A WebView with none of them handled
 * is what people mean when they say an app "feels like a website in a box": back closes it from
 * anywhere, the status bar is a white band over a dark product, the keyboard covers the field being
 * typed into, and the splash hangs for a fixed time whether or not the app is ready.
 *
 * Shared by Android and iOS, because all four concerns are the same concern on both, and the two
 * places they genuinely differ are handled by the plugins themselves: `backButton` only ever fires
 * on Android (iOS has no hardware back), and the keyboard resize mode is a no-op where it does not
 * apply. Writing one file rather than two is what keeps the two platforms from drifting.
 *
 * Called once at startup and a no-op on the web, where none of these plugins exist. Every step is
 * individually guarded, so one unavailable plugin degrades that one behaviour rather than
 * abandoning the rest of the setup.
 */

import { isAndroid, isNative } from "./platform";
import { syncStorePurchases } from "./storeBilling";
import { isAuthReturnUrl } from "./authRedirect";

/** Nuvra's dark surface. Matches the splash background in capacitor.config.ts. */
const SURFACE = "#0A0A0B";

async function setUpStatusBar(): Promise<void> {
  const { StatusBar, Style } = await import("@capacitor/status-bar");
  // Light content, because the product is dark. Getting this wrong gives dark icons on a dark bar,
  // which reads as a rendering fault rather than a theme choice. On iOS this needs
  // UIViewControllerBasedStatusBarAppearance set to false in Info.plist, or it silently does
  // nothing.
  await StatusBar.setStyle({ style: Style.Dark });

  if (isAndroid()) {
    // Android only. iOS has no settable status bar background: the page simply draws behind it,
    // and calling this there throws rather than being ignored.
    await StatusBar.setBackgroundColor({ color: SURFACE });
  }

  // The window draws behind the bar on both platforms. Android enforces edge to edge from targetSdk
  // 35 and iOS always has, so this makes the intent explicit rather than leaving it to the version
  // the app happens to be running on.
  //
  // What reserves the space differs per platform, and getting this wrong is what put the header
  // under the clock in version code 43. On iOS the CSS insets are real and index.css consumes them.
  // On Android they are NOT: the WebView derives env(safe-area-inset-top) from the display cutout
  // and never reports the status bar, so the spacing is done natively instead, by
  // `adjustMarginsForEdgeToEdge: "force"` in capacitor.config.ts. Leaving this call in place
  // without that option is the exact combination that ships a broken layout.
  await StatusBar.setOverlaysWebView({ overlay: true });
}

async function setUpBackButton(): Promise<void> {
  // Registered unconditionally: the event only exists on Android, so on iOS this listener is simply
  // never called. Guarding it would add a branch that protects nothing.
  const { App } = await import("@capacitor/app");
  await App.addListener("backButton", ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
      return;
    }
    // At the root, back exits. Android users expect that, and swallowing it produces an app that
    // cannot be left without the task switcher.
    void App.exitApp();
  });
}

/**
 * Complete a sign-in that came back through the app's own URL scheme.
 *
 * On the web the auth client sees `?code=` in the address bar and exchanges it for a session on its
 * own. A native return never touches the address bar: the OS hands the URL to the app as an event,
 * so nothing would notice it and the customer would sit on the sign-in screen having already
 * authenticated successfully somewhere they can no longer see.
 *
 * The code is exchanged explicitly, and only for URLs that are recognisably ours. An unrecognised
 * deep link is left alone for ordinary routing, because feeding arbitrary incoming URLs to the auth
 * client is how a malicious link becomes a session.
 */
/**
 * Sign-in returns that have already been handled.
 *
 * The same callback can arrive more than once: Android re-delivers an intent, and a cold start
 * surfaces the launch URL through BOTH getLaunchUrl() and the appUrlOpen listener. Exchanging one
 * code twice fails the second time (the code is single use) and the failure looks exactly like a
 * broken sign-in, so the URL is claimed once and never processed again.
 */
const handledAuthReturns = new Set<string>();

async function completeAuthReturn(url: string): Promise<void> {
  if (!url || !isAuthReturnUrl(url)) return;
  if (handledAuthReturns.has(url)) return;
  handledAuthReturns.add(url);

  const { supabase } = await import("@/integrations/supabase/client");
  try {
    // Custom-scheme URLs are not parseable as https, so the query and fragment are read from the
    // raw string. PKCE puts `code` in the query; an older implicit response puts tokens in the
    // fragment; an error from the provider arrives in either.
    const query = new URLSearchParams(url.split("?")[1]?.split("#")[0] ?? "");
    const fragment = new URLSearchParams(url.split("#")[1] ?? "");

    const code = query.get("code");
    if (code) {
      await supabase.auth.exchangeCodeForSession(code);
      return;
    }

    const accessToken = fragment.get("access_token");
    const refreshToken = fragment.get("refresh_token");
    if (accessToken && refreshToken) {
      await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
    }
    // No code and no tokens means the provider returned an error or the customer cancelled. The
    // sign-in screen is still on display and still usable, so there is nothing to do.
  } catch {
    // A failed exchange leaves the customer on the sign-in screen, which is the honest state.
    // Throwing here would take the whole shell down for a recoverable sign-in problem.
  }
}

async function setUpAuthReturn(): Promise<void> {
  const { App } = await import("@capacitor/app");

  // THE COLD START, which is the case that was losing sessions.
  //
  // Signing in happens in an EXTERNAL browser, because Google refuses to do it inside a WebView.
  // While the customer is over there, Android is free to kill Nuvra to reclaim memory, and it
  // frequently does. The callback then arrives as the LAUNCH intent of a brand new process, and a
  // listener registered a few awaits into startup is not there to catch it. getLaunchUrl is the
  // documented way to read that URL, and nothing in this repository was calling it, so a customer
  // who was returned to a cold app authenticated successfully in the browser and arrived back at
  // the sign-in screen. That is precisely the reported fault.
  //
  // Read first, listen second: both paths funnel into the same idempotent handler, so the overlap
  // between them is harmless.
  try {
    const launch = await App.getLaunchUrl();
    if (launch?.url) await completeAuthReturn(launch.url);
  } catch {
    // Not every platform implements it. An absent launch URL is not an error, and the listener
    // below still covers a warm return.
  }

  await App.addListener("appUrlOpen", ({ url }) => {
    void completeAuthReturn(url);
  });
}

async function setUpKeyboard(): Promise<void> {
  const { Keyboard, KeyboardResize } = await import("@capacitor/keyboard");
  // Resize the WebView rather than pan it. Nuvra's composer sits at the bottom of the screen, and
  // panning slides the whole page up so the header leaves the screen while typing.
  await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
  await Keyboard.setScroll({ isDisabled: true });
}

async function hideSplash(): Promise<void> {
  const { SplashScreen } = await import("@capacitor/splash-screen");
  // Hidden when the app has actually painted, not on a timer. The config's duration is only a
  // backstop for a startup that fails before reaching this line.
  await SplashScreen.hide();
}

let started = false;

export async function initNativeShell(): Promise<void> {
  if (started || !isNative()) return;
  started = true;

  const steps: Array<() => Promise<void>> = [
    setUpStatusBar,
    setUpBackButton,
    // Before the splash is hidden, so a sign-in return that arrives during a cold start is not
    // dropped in the window between the app appearing and the listener existing.
    setUpAuthReturn,
    setUpKeyboard,
    hideSplash,
  ];

  for (const step of steps) {
    // One failed plugin must not take the others with it, and none of them may take the app with
    // them: a shell that cannot set its status bar colour still has to start.
    await step().catch(() => {});
  }

  // Purchases that completed on the device but never reached the server are re-sent here. Not
  // awaited: it is a background reconciliation, and the app must not wait on the network to start.
  void syncStorePurchases();
}
